import React, { lazy, Suspense, useEffect, useState } from "react";
import { Switch, Route, Redirect, useLocation } from "react-router-dom";
import queryString from "query-string";
import LoadingBar from "react-top-loading-bar";
import { useAuthState } from "Contexts/Auth";
import ApprovedOrders from "Pages/ApprovedOrders";
import userType from "Types/userType";
import { Typography } from "antd";

const Home = () => {
  const { isLoggedIn } = useAuthState();
  return <Redirect to={isLoggedIn ? "/chemicals/list" : "/login"} />;
};

const ChemicalList = lazy(() => import("Pages/ChemicalsList"));
const ChemicalEditor = lazy(() => import("Pages/ChemicalEditor"));
const UsersList = lazy(() => import("Pages/UsersList"));
const UserEditor = lazy(() => import("Pages/UserEditor"));
const NotFound = lazy(() => import("Pages/404"));
const Units = lazy(() => import("Pages/Units"));
const WaitingOrders = lazy(() => import("Pages/WaitingOrders"));
const RejectedOrders = lazy(() => import("Pages/RejectedOrders"));
const AllOrders = lazy(() => import("Pages/AllOrders"));

const ProtectedRoute: React.FC<{
  component: React.FunctionComponent;
  isLoggedIn: boolean;
  path: string;
  requiredRoles?: userType["role"][];
}> = ({ isLoggedIn, component: Component, requiredRoles, ...rest }) => {
  const location = useLocation();
  const { role } = useAuthState();
  const { returnUrl } = queryString.parse(location.search);
  return (
    <Route
      {...rest}
      render={() =>
        isLoggedIn ? (
          !requiredRoles || requiredRoles.find((r) => r === role) ? (
            <Component />
          ) : (
            <Typography.Text>مجوز دسترسی به این صفحه را ندارید</Typography.Text>
          )
        ) : (
          <Redirect to={returnUrl ? returnUrl + "" : "/"} />
        )
      }
    />
  );
};

const routes: {
  path: string;
  component: React.FunctionComponent;
  isProtected?: boolean;
  isNotProtected?: boolean;
  exact?: boolean;
  requiredRoles?: userType["role"][];
}[] = [
  {
    path: "/",
    component: Home,
    exact: true,
  },
  { isProtected: true, path: "/chemicals/list", component: ChemicalList },
  {
    requiredRoles: ["Director", "Laboratory"],
    isProtected: true,
    path: "/chemicals/addEdit/:chemicalId?",
    component: ChemicalEditor,
  },
  {
    isProtected: true,
    path: "/users/list",
    component: UsersList,
    requiredRoles: ["Director"],
  },
  {
    requiredRoles: ["Director"],
    isProtected: true,
    path: "/users/addEdit/:userId?",
    component: UserEditor,
  },
  {
    requiredRoles: ["Director", "Laboratory"],
    isProtected: true,
    path: "/units",
    component: Units,
  },
  { isProtected: true, path: "/orders/waiting", component: WaitingOrders },
  { isProtected: true, path: "/orders/all", component: AllOrders },
  { isProtected: true, path: "/orders/approved", component: ApprovedOrders },
  { isProtected: true, path: "/orders/rejected", component: RejectedOrders },
];

const Routes: React.FC = () => {
  const { isLoggedIn } = useAuthState();
  const [loading, setLoading] = useState(0);
  return (
    <>
      <LoadingBar
        progress={loading}
        height={2}
        color="red"
        onLoaderFinished={() => {
          setLoading(0);
          return {};
        }}
      />
      <Suspense fallback={<Loading setLoading={setLoading} />}>
        <Switch>
          {routes.map((r) => {
            if (r.isProtected || r.requiredRoles)
              return (
                <ProtectedRoute
                  key={r.path}
                  isLoggedIn={isLoggedIn}
                  component={r.component}
                  requiredRoles={r.requiredRoles}
                  path={r.path}
                />
              );
            if (r.isNotProtected)
              return (
                <ProtectedRoute
                  key={r.path}
                  isLoggedIn={!isLoggedIn}
                  component={r.component}
                  path={r.path}
                />
              );
            return (
              <Route
                key={r.path}
                exact={!!r.exact}
                component={r.component}
                path={r.path}
              />
            );
          })}
          <Route component={NotFound} />
        </Switch>
      </Suspense>
    </>
  );
};

export default Routes;

const Loading = (p: any) => {
  useEffect(() => {
    const tId = setTimeout(() => {
      p.setLoading(50);
    }, 300);
    return () => {
      clearTimeout(tId);
      p.setLoading(100);
    };
  }, [p]);
  return <></>;
};
